import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { PropertyUnitsService } from 'src/app/features/property-units/services/property-units.service';
import { ClientsService } from 'src/app/features/customers/services/clients.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddClientComponent } from 'src/app/shared/components/add-client/add-client.component';
import { DynamicServices } from 'src/app/features/ayen-services/services/dynamic-services/dynamic-services.service';
import { AdminOrdersService } from '../../services/orders/admin-orders.service';
import { CompanyServicesService } from 'src/app/features/companies/services/company-services/company-services.service';
import { DropdownOption } from 'src/app/shared/Models/dropdown-option.model';

@Component({
  selector: 'property-building-details',
  standalone: false,
  templateUrl: './property-building-details.component.html',
  styleUrls: ['./property-building-details.component.scss'],
})
export class PropertyBuildingDetailsComponent implements OnInit {
  @Input() orderId: any;
  @Input() orderData: any;
  @Output() errorMsg = new EventEmitter();
  @Output() building_view = new EventEmitter();

  form: FormGroup;
  spinner: boolean = false;
  alertmsg: boolean = false;
  errorMessage: string = '';
  
  // View switching for add unit
  currentView: 'main' | 'add-unit' = 'main';
  pendingNewUnitId: number | null = null;
  pendingPackageId: number | null = null;
  
  // Store order data when loading existing order
  order: any;

  // Services dropdown data
  services: any[] = [];
  servicesList: any[] = [];
  servicesPageCount: number = 0;
  selectedService: any;

  // Customers dropdown data
  clients: any[] = [];
  clientsList: any[] = [];
  clientsPageCount: number = 0;
  selectedClient: any;

  // Customer's units (loaded after service + customer selection)
  customerUnits: any[] = [];
  showUnitsDropdown: boolean = false;

  // Available packages (loaded after unit selection)
  availablePackages: any[] = [];
  showPackagesDropdown: boolean = false;

  // Reference numbers (linked orders)
  mainOrders: any[] = [];
  referenceNumberList: any[] = [];
  refrencePageCount: number = 0;
  selectedRefrenceNumber: any;

  // Real estate developers (companies)
  companies: DropdownOption[] = [];
  companiesPage: number = 1;
  companiesPageCount: number = 0;
  companiesSearchTerm: string = '';

  // Company contracts
  contracts: any[] = [];
  contractsPage: number = 1;
  contractsPageCount: number = 0;
  contractsSearchTerm: string = '';

  public clientDialogRef!: MatDialogRef<AddClientComponent>;

  constructor(
    private fb: FormBuilder,
    private clientsService: ClientsService,
    private dialog: MatDialog,
    private dynamicServices: DynamicServices,
    private router: Router,
    private route: ActivatedRoute,
    private propertyUnitsService: PropertyUnitsService,
    private adminOrdersService: AdminOrdersService,
    private company: CompanyServicesService,
  ) {
    this.form = this.fb.group({
      service_id: [null, Validators.required],
      customer_id: [null, Validators.required],
      property_unit_id: [null],
      package_id: [null],
      linked_order: [null],
      linked_real_estate_project_developer: [null],
      contract: [null],
    });
  }

  ngOnInit(): void {
    this.loadServices();
    this.loadClients();
    this.loadCompanies();
    this.setupFormListeners();
    
    this.route.queryParams.subscribe((params) => {
      if (params['id']) {
        this.orderId = +params['id'];
        if (this.orderId) {
          this.getAdminOrder();
        }
      }
    });
  }

  setServiceData(service: any): void {
    this.selectedService = service?.id || service?.pk;
    this.form.patchValue({ service_id: service?.id || service?.pk });
  }

  // Initial load with caching
  loadServices(): void {
    const servicesParams = {
      page: 1,
      page_size: 20,
      is_active: true,
    };

    this.dynamicServices.getServices(servicesParams).subscribe({
      next: (data) => {
        this.services = data.results;
        this.servicesList = [...this.services];
        this.servicesPageCount = data.pages_count;
      },
      error: (error) => console.error('Error loading dynamic services:', error),
    });
  }

  getDynamicServices(params: any): void {
    let servicesParams = { ...params.params };
    servicesParams['is_active'] = true;

    if (params.clearList && !servicesParams.query) {
      if (this.services.length > 0) {
        this.servicesList = [...this.services];
        return;
      }
      this.loadServices();
      return;
    }

    // Handle search queries
    if (params.clearList) {
      servicesParams['page_size'] = 20;
      this.services = [];
      this.servicesList = [];
    } else {
      // Pagination
      servicesParams['page_size'] = 20;
    }

    this.dynamicServices.getServices(servicesParams).subscribe({
      next: (data) => {
        if (params.clearList) {
          this.services = data.results;
        } else {
          this.services.push(...data.results);
        }
        this.servicesList = [...this.services];
        this.servicesPageCount = data.pages_count;
      },
      error: (error) => console.error('Error loading dynamic services:', error),
    });
  }

  setClientData(client: any): void {
    this.selectedClient = client?.id || client?.pk;
    this.form.patchValue({ customer_id: client?.id || client?.pk });
    
    // Reset units and packages when client changes or is cleared
    if (!client) {
      this.customerUnits = [];
      this.showUnitsDropdown = false;
      this.availablePackages = [];
      this.showPackagesDropdown = false;
      this.form.patchValue({ 
        property_unit_id: null,
        package_id: null 
      });
    }
  }

  // Initial load with caching
  loadClients(): void {
    const clientsParams = {
      page: 1,
      page_size: 20,
    };

    this.clientsService.getAllClients(clientsParams).subscribe({
      next: (data) => {
        this.clients = data.results;
        this.clientsList = [...this.clients];
        this.clientsPageCount = data.pages_count;
      },
      error: (error) => console.error('Error loading clients:', error),
    });
  }

  getDynamicClients(params: any): void {
    let clientsParams = { ...params.params };
    if (clientsParams.query) {
      clientsParams.full_name = clientsParams.query;
      delete clientsParams.query;
    }

    if (params.clearList && !clientsParams.full_name) {
      if (this.clients.length > 0) {
        this.clientsList = [...this.clients];
        return;
      }
      this.loadClients();
      return;
    }

    if (params.clearList) {
      clientsParams['page_size'] = 20;
      this.clientsList = [];
    } else {
      clientsParams['page_size'] = 20;
    }

    this.clientsService.getAllClients(clientsParams).subscribe({
      next: (data) => {
        if (params.clearList) {
          this.clientsList = data.results;
        } else {
          this.clientsList = [...this.clientsList, ...data.results];
        }
        this.clientsPageCount = data.pages_count;
      },
      error: (error) => console.error('Error loading clients:', error),
    });
  }

  addClient(): void {
    this.clientDialogRef = this.dialog.open(AddClientComponent, {
      disableClose: true,
    });

    this.clientDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadClients();
        this.selectedClient = result?.id || result?.pk;
        this.form.patchValue({ customer_id: result?.id || result?.pk });
      }
    });
  }

  setupFormListeners(): void {
    this.form.get('service_id')?.valueChanges.subscribe(() => {
      this.checkAndLoadUnits();
    });

    this.form.get('customer_id')?.valueChanges.subscribe(() => {
      this.checkAndLoadUnits();
    });

    // Listen for unit selection to load packages
    this.form.get('property_unit_id')?.valueChanges.subscribe((unitId) => {
      if (unitId) {
        this.loadPackagesForSelectedUnit(unitId);
      } else {
        this.showPackagesDropdown = false;
        this.availablePackages = [];
      }
    });

    this.form.get('contract')?.valueChanges.subscribe((contractId) => {
      if (contractId) {
        this.selectedRefrenceNumber = null;
        this.referenceNumberList = [];
        this.mainOrders = [];
        this.form.patchValue({ linked_order: null }, { emitEvent: false });
        this.loadReferenceNumbers();
      } else {
        this.selectedRefrenceNumber = null;
        this.referenceNumberList = [];
        this.mainOrders = [];
        this.form.patchValue({ linked_order: null }, { emitEvent: false });
      }
    });
  }

  checkAndLoadUnits(): void {
    const serviceId = this.form.get('service_id')?.value;
    const customerId = this.form.get('customer_id')?.value;

    // Only reset unit and package selections if we're not loading an existing order
    if (!this.pendingNewUnitId && !this.pendingPackageId) {
      this.form.patchValue(
        {
          property_unit_id: null,
          package_id: null,
        },
        { emitEvent: false },
      );
    }

    if (serviceId && customerId) {
      this.loadCustomerUnits(serviceId, customerId);
    } else {
      // Hide units dropdown if selections are incomplete
      this.showUnitsDropdown = false;
      this.showPackagesDropdown = false;
      this.customerUnits = [];
      this.availablePackages = [];
    }
  }

  loadCustomerUnits(serviceId: number, customerId: number): void {
    this.spinner = true;

    this.propertyUnitsService.loadCustomerUnits(serviceId, customerId).subscribe({
      next: (response) => {
        // Store units with their packages
        this.customerUnits = response.map((unit: any) => ({
          ...unit,
          packages: unit.packages || [] // Keep packages array with each unit
        }));
        this.showUnitsDropdown = true;
        this.spinner = false;
        
        // Auto-select the unit if we have a pending ID
        if (this.pendingNewUnitId) {
          const newUnit = this.customerUnits.find(unit => unit.id === this.pendingNewUnitId);
          if (newUnit) {
            this.form.patchValue({ property_unit_id: this.pendingNewUnitId });
            this.pendingNewUnitId = null; // Clear the pending ID
          }
        }
      },
      error: (error) => {
        this.spinner = false;
        this.showUnitsDropdown = false;
      }
    });
  }

  addNewUnit(): void {
    // Switch to add-unit view instead of navigating
    this.currentView = 'add-unit';
  }

  loadPackagesForSelectedUnit(unitId: number): void {
    // Find the selected unit from customerUnits
    const selectedUnit = this.customerUnits.find(unit => unit.id === unitId);
    
    if (selectedUnit && selectedUnit.packages) {
      
      // Use packages from the unit response
      this.availablePackages = selectedUnit.packages.map((pkg: any) => ({
        id: pkg.id,
        name: pkg.name,
        price: pkg.cost,
        starting_price: pkg.cost,
        description: pkg.description || '',
        package_title: pkg.package_title || '',
        order_type: pkg.order_type || '',
        dynamic_service: pkg.dynamic_service,
        building_type: pkg.building_type,
        package_meters: pkg.package_meters,
        cost_unit: pkg.cost_unit,
        ayen_extra_meter_cost: pkg.ayen_extra_meter_cost,
        best_choices: pkg.best_choices,
        features: pkg.features || []
      }));
      
      this.showPackagesDropdown = true;
      
      // Auto-select package if we have a pending package ID
      if (this.pendingPackageId) {
        const packageExists = this.availablePackages.find(pkg => pkg.id === this.pendingPackageId);
        if (packageExists) {
          this.form.patchValue({ package_id: this.pendingPackageId });
          this.pendingPackageId = null; // Clear after selection
        }
      }
    } else {
      // No packages found for this unit
      this.availablePackages = [];
      this.showPackagesDropdown = true;
    }
  }

  selectUnit(unit: any): void {
    this.form.patchValue({ property_unit_id: unit.id });
  }

  selectPackage(pkg: any): void {
    this.form.patchValue({ package_id: pkg.id });
  }

  save(): void {
    this.alertmsg = false;
    if (this.form.valid) {
      this.spinner = true;

      // Get selected package details to extract order info
      const selectedPackage = this.availablePackages.find(
        pkg => pkg.id === this.form.value.package_id
      );

      // Build order payload
      const orderPayload = {
        order_client: this.form.value.customer_id,
        dynamic_service: this.form.value.service_id,
        property_unit: this.form.value.property_unit_id,
        package: this.form.value.package_id,
        order_type: selectedPackage?.order_type || null,
        building_type: selectedPackage?.building_type || null,
        linked_order: this.form.value.linked_order,
        linked_real_estate_project_developer: this.form.value.linked_real_estate_project_developer,
        contract: this.form.value.contract,
      };
      const orderObservable = this.orderId 
        ? this.adminOrdersService.updateOrder(this.orderId, orderPayload)
        : this.adminOrdersService.createNewOrder(orderPayload);


      orderObservable.subscribe({
        next: (response) => {
          this.spinner = false;
          
          // Only update URL params if this was a new order creation
          if (!this.orderId) {
            const params = { id: response.id };
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: params,
              queryParamsHandling: '',
            });
            this.orderId = response.id;
          }
          
          // Emit the view change with the order ID
          this.building_view.emit({
            view: 'address',
            id: this.orderId || response.id,
            dynamic_service: response.dynamic_service || this.form.value.service_id,
          });
          this.showConfirmationModal();
        },
        error: (error) => {
          console.error('Error saving order:', error);
          this.spinner = false;
          this.alertmsg = true;
          this.errorMessage = 
            Object.values(error.error || {})[0]?.toString() || 
            'حدث خطأ أثناء حفظ الطلب';
        },
      });
    } else {
      this.alertmsg = true;
      this.errorMessage = 'يوجد خطأ في بعض البيانات';
    }
  }

  showConfirmationModal(): void {
    this.errorMsg.emit({
      message: 'تم حفظ البيانات بنجاح.',
      imgSuccess: true,
    });
  }

  hideErrorMSG(): void {
    this.alertmsg = false;
  }
  
  // Handle unit added event from add-property-unit component
  onUnitAdded(event: any): void {
    this.pendingNewUnitId = event.id;
    
    // Return to main view
    this.currentView = 'main';
    
    // Reload customer units to include the new one
    const serviceId = this.form.get('service_id')?.value;
    const customerId = this.form.get('customer_id')?.value;
    
    if (serviceId && customerId) {
      this.loadCustomerUnits(serviceId, customerId);
    }
  }
  
  onAddUnitCancelled(): void {
    this.currentView = 'main';
  }
  
  // Get existing order data
  getAdminOrder(): void {
    this.spinner = true;
    this.adminOrdersService.getAdminOrder(this.orderId).subscribe({
      next: (data) => {
        this.order = data;
        this.patchValues();
        this.spinner = false;
      },
      error: (error) => {
        this.spinner = false;
      }
    });
  }
  
  // Patch form with existing order values
  patchValues(): void {
    if (!this.order) return;
    
    // Set pending IDs FIRST before patching any form values
    // Check if property_unit is an ID (number) or an object with id property
    if (this.order.property_unit) {
      if (typeof this.order.property_unit === 'object' && this.order.property_unit.id) {
        this.pendingNewUnitId = this.order.property_unit.id;
      } else if (typeof this.order.property_unit === 'number') {
        this.pendingNewUnitId = this.order.property_unit;
      }
    }
    
    // Check if package is an ID (number) or an object with id property
    if (this.order.package) {
      if (typeof this.order.package === 'object' && this.order.package.id) {
        this.pendingPackageId = this.order.package.id;
      } else if (typeof this.order.package === 'number') {
        this.pendingPackageId = this.order.package;
      }
    }
    
    // Patch customer (with emitEvent: false to prevent triggering listeners)
    if (this.order.order_details?.order_client_object?.id) {
      const clientObj = {
        id: this.order.order_details.order_client_object.id,
        full_name: this.order.order_details.order_client_object.name,
      };
      this.clients = [];
      this.clients.push(clientObj);
      this.clientsList = [...this.clients];
      this.selectedClient = clientObj.id;
      this.form.patchValue({ customer_id: clientObj.id }, { emitEvent: false });
    }
    
    // Patch service (with emitEvent: false to prevent triggering listeners)
    if (this.order.dynamic_service) {
      const serviceObj = {
        id: this.order.order_details.dynamic_service.id,
        name: this.order.order_details.dynamic_service.name,
      };
      this.services = [];
      this.services.push(serviceObj);
      this.servicesList = [...this.services];
      this.selectedService = serviceObj.id;
      this.form.patchValue({ service_id: serviceObj.id }, { emitEvent: false });
    }
    
    // Load customer units if we have both service and customer
    // This will auto-select the unit and trigger package loading
    if (this.order.dynamic_service && this.order.order_details?.order_client_object?.id) {
      this.loadCustomerUnits(
        this.order.dynamic_service,
        this.order.order_details.order_client_object.id
      );
    }
  }

  // Reference number (linked order) methods
  loadReferenceNumbers(): void {
    const contractId = this.form.get('contract')?.value;
    if (!contractId) {
      return;
    }

    const params: any = { 
      page: 1, 
      page_size: 100, // Load more items since we're caching
      contract: contractId 
    };

    this.adminOrdersService.getRefrenceNumbers(params).subscribe({
      next: (data) => {
        this.mainOrders = data.results || [];
        this.referenceNumberList = [...this.mainOrders];
        this.refrencePageCount = data.pages_count;
      },
      error: (error) => {
        console.error('Error loading reference numbers:', error);
        this.referenceNumberList = [];
        this.mainOrders = [];
      },
    });
  }

  getRefrenceNumberByParams(params: { clearList?: boolean; params?: Record<string, unknown> }): void {
    if (!params) {
      return;
    }
    
    const contractId = this.form.get('contract')?.value;
    if (!contractId) {
      return;
    }

    if (params.clearList) {
      const refParams: any = {
        page: 1,
        page_size: 20,
        contract: contractId
      };
      
      this.adminOrdersService.getRefrenceNumbers(refParams).subscribe({
        next: (data) => {
          this.mainOrders = data.results || [];
          this.referenceNumberList = [...this.mainOrders];
          this.refrencePageCount = data.pages_count || 0;
        },
        error: (error) => {
          console.error('Error loading reference numbers:', error);
          this.referenceNumberList = [];
          this.mainOrders = [];
        },
      });
      return;
    }

    // This handles pagination (loading additional pages)
    let refParams = { ...(params.params || {}) };
    refParams['page_size'] = 20;
    refParams['contract'] = contractId;
    
    this.adminOrdersService.getRefrenceNumbers(refParams).subscribe({
      next: (data) => {
        this.mainOrders.push(...(data.results || []));
        this.referenceNumberList = [...this.mainOrders];
        this.refrencePageCount = data.pages_count || 0;
      },
      error: (error) => console.error('Error loading reference numbers:', error),
    });
  }

  setRefrenceData(linked_order: { id: number } | null): void {
    this.selectedRefrenceNumber = linked_order?.id;
    this.form.patchValue({ linked_order: linked_order?.id });
  }

  loadCompanies(searchTerm: string = ''): void {
    const params: any = { page: this.companiesPage, page_size: 20 };
    if (searchTerm) {
      params.query = searchTerm;
    }

    this.company.getCompaniesList(params).subscribe({
      next: (response: any) => {
        if (this.companiesPage === 1) {
          this.companies = response?.results || [];
        } else {
          this.companies = [...this.companies, ...(response?.results || [])];
        }
        this.companiesPageCount = response?.pages_count || 0;
      },
      error: (error) => console.error('Error loading companies:', error),
    });
  }

  onCompanyScroll(): void {
    if (this.companiesPage < this.companiesPageCount) {
      this.companiesPage++;
      this.loadCompanies(this.companiesSearchTerm);
    }
  }

  onCompanySearch(event: any): void {
    this.companiesSearchTerm = event.term || '';
    this.companiesPage = 1;
    this.loadCompanies(this.companiesSearchTerm);
  }

  companyChanged(event: any): void {
    if (event) {
      this.getCompanyContracts(event);
    } else {
      this.contracts = [];
      this.form.patchValue({ contract: null });
    }
  }

  // Company Contracts methods
  getCompanyContracts(companyId: number): void {
    this.contractsPage = 1;
    this.contractsSearchTerm = '';
    this.contracts = [];
    this.loadCompanyContracts(companyId);
  }

  loadCompanyContracts(companyId: number, searchTerm: string = ''): void {
    const params: any = { page: this.contractsPage, page_size: 20 };
    if (searchTerm) {
      params.query = searchTerm;
    }

    this.company.getCompanyContracts(companyId, params).subscribe({
      next: (response: any) => {
        if (this.contractsPage === 1) {
          this.contracts = response?.results || [];
        } else {
          this.contracts = [...this.contracts, ...(response?.results || [])];
        }
        this.contractsPageCount = response?.pages_count || 0;
      },
      error: (error) => {
        console.error('Error loading contracts:', error);
        this.contracts = [];
      }
    });
  }

  onContractScroll(): void {
    const companyId = this.form.get('linked_real_estate_project_developer')?.value;
    if (companyId && this.contractsPage < this.contractsPageCount) {
      this.contractsPage++;
      this.loadCompanyContracts(companyId, this.contractsSearchTerm);
    }
  }

  onContractSearch(event: any): void {
    const companyId = this.form.get('linked_real_estate_project_developer')?.value;
    if (companyId) {
      this.contractsSearchTerm = event.term || '';
      this.contractsPage = 1;
      this.loadCompanyContracts(companyId, this.contractsSearchTerm);
    }
  }
}
